import { getEffectiveDate } from "../../../../utils/effectiveDate";
import type {
  ExpenseBreakdownTimeframe,
  ExpenseBreakdownUnavailableReason,
} from "../../domain/entities/ExpenseBreakdownEntity";

function formatRangeDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function truncateToLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Parse API YYYY-MM-DD in local calendar (avoids UTC shift). */
function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return null;
  }
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
    return null;
  }
  return dt;
}

export interface ExpenseBreakdownApiRange {
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  breakdownUnavailableReason?: ExpenseBreakdownUnavailableReason | null;
}

function unavailableSuffix(
  reason: ExpenseBreakdownUnavailableReason | null | undefined,
): string {
  if (reason === "no_closed_books") {
    return "No closed books available";
  }
  if (reason === "no_closed_months_in_fy") {
    return "No closed months in this FY yet";
  }
  return "—";
}

/**
 * Label for the expense breakdown window. Prefer API `dateRangeStart` / `dateRangeEnd`
 * (matches aggregation for breakdown and totals); fall back to effective date for legacy payloads.
 */
export function getExpenseBreakdownDateRangeSubtext(
  timeframe: ExpenseBreakdownTimeframe,
  api?: ExpenseBreakdownApiRange | null,
): string {
  const startStr = api?.dateRangeStart;
  const endStr = api?.dateRangeEnd;
  if (typeof startStr === "string" && startStr.length > 0) {
    const start = parseYmdLocal(startStr);
    if (start) {
      if (typeof endStr === "string" && endStr.length > 0) {
        const end = parseYmdLocal(endStr);
        if (end) {
          return `${formatRangeDate(start)} – ${formatRangeDate(end)}`;
        }
      }
      if (timeframe === "ytd") {
        return `${formatRangeDate(start)} – ${unavailableSuffix(api?.breakdownUnavailableReason)}`;
      }
    }
  }

  const effective = truncateToLocalDay(getEffectiveDate());

  if (timeframe === "last_month") {
    const firstOfThisMonth = new Date(
      effective.getFullYear(),
      effective.getMonth(),
      1,
    );
    const lastDayOfPrevMonth = new Date(firstOfThisMonth);
    lastDayOfPrevMonth.setDate(0);
    const firstOfLastMonth = new Date(
      lastDayOfPrevMonth.getFullYear(),
      lastDayOfPrevMonth.getMonth(),
      1,
    );
    return `${formatRangeDate(firstOfLastMonth)} – ${formatRangeDate(lastDayOfPrevMonth)}`;
  }

  const y = effective.getFullYear();
  const m = effective.getMonth() + 1;
  const fyStartYear = m >= 4 ? y : y - 1;
  const start = new Date(fyStartYear, 3, 1);
  return `${formatRangeDate(start)} – ${formatRangeDate(effective)}`;
}

export function getExpenseBreakdownUnavailableUserMessage(
  reason: ExpenseBreakdownUnavailableReason | null | undefined,
): string | null {
  if (reason === "no_closed_books") {
    return "No closed accounting month is available yet.";
  }
  if (reason === "no_closed_months_in_fy") {
    return "No closed months in the current financial year yet.";
  }
  return null;
}
